import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { configData } from "../../config";
import {
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Container,
  Card,
  Image,
} from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSubscriptionNumber,
  changeStartDate,
  changeExpiresAfter,
  updateTotals,
  resetSubscription,
  changePartyName,
  changePartyMobile,
  changePartyAddress,
  changeType,
  changeInterval,
  changeIntervalUnit,
  changeDonationItemPrice,
  changeDonationItemTaxCode,
} from "../store/createDonationSubscriptionSlice";
import { regionCode } from "../../States";
import { taxList } from "../../shared/config/taxConfig";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import { setAccessToken, setTenant } from "../../Auth/store/authSlice";
import { fetchSetting } from "../../Settings/store/settingSlice";
import errorImage from "../../assets/images/error.png";
import { cloneDeep } from "lodash";
import FooterSection from "../../enquiries/components/FooteSection";

const DonationFormPage = () => {
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const subscriptionState = useSelector((state) => state.createSubscription);
  const businessDetails = useSelector(
    (state) => state?.getSetting?.dataSetting
  );
  const [donationPackages, setDonationPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tenantId, setTenantId] = useState(false);
  const [token, setToken] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [taxRate, setTaxRate] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [total, setTotal] = useState(0);
  const [price, setPrice] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [expiresAfter, setExpiresAfter] = useState(0);
  const [response, setResponse] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encryptedValue = params.get("data");
    if (encryptedValue) {
      decryptData(encryptedValue);
    } else {
      setShowError(true);
    }
  }, []);

  const decryptData = async (formData) => {
    try {
      const response = await axios.post(
        `${configData.API_URL}tenants/verify-donations`,
        { data: formData }
      );

      setTenantId(response?.data?.tenant?.id);
      setToken(response?.data?.decryptedData?.access_token);

      dispatch(setTenant(response.data?.tenant?.id));
      dispatch(setAccessToken(response.data?.decryptedData?.access_token));

      if (response.data.donationPackage) {
        setDonationPackages(response.data.donationPackage);
      } else if (response.data.donationPackage) {
        setDonationPackages([response.data.donationPackage]);
      } else {
        setDonationPackages([]);
      }
    } catch (err) {
      console.error("Error decrypting data:", err);
    }
  };

  useEffect(() => {
    dispatch(
      fetchSetting({
        accessToken: token,
        tenant_id: tenantId,
      })
    );
  }, [tenantId, token]);

  useEffect(() => {
    if (tenantId && token) {
      dispatch(fetchSubscriptionNumber({ tenantId, token }));
    }
  }, [tenantId, token]);

  const findState =
    regionCode.length > 0 &&
    regionCode.find((value) => value.id == businessDetails?.state);

  useEffect(() => {
    if (startDate) {
      dispatch(changeStartDate(startDate.toISOString()));
    }
  }, [startDate]);

  useEffect(() => {
    dispatch(changeExpiresAfter(expiresAfter));
  }, [expiresAfter]);

  const calculateTaxAndTotal = (amount, taxCode, isTaxIncluded) => {
    const selectedTaxOption = taxList.find((option) => option.code === taxCode);

    if (!selectedTaxOption) {
      setTaxRate(0);
      setTaxAmount(0);
      setTotal(parseFloat(amount));

      const subscriptionItem = {
        donation_package_id: donationPackages?.id,
        donation_package_name: donationPackages?.name,
        donation_package_code: donationPackages?.code,
        quantity: 1,
        price: amount,
        tax_rate: 0,
        tax_amount: 0,
        total: parseFloat(amount),
        tax_code: taxCode ?? "None",
        is_tax_included: donationPackages?.is_tax_included,
        cgst: 0,
        sgst: 0,
      };

      dispatch(updateTotals(subscriptionItem));
      return;
    }

    const ratePercentage = selectedTaxOption.rate;
    const rate = (amount * ratePercentage) / 100;
    setTaxRate(rate);

    let calculatedTaxAmount = 0;
    let calculatedTotal = 0;

    if (isTaxIncluded == 1) {
      calculatedTaxAmount = (amount * ratePercentage) / (100 + ratePercentage);
      calculatedTotal = amount - calculatedTaxAmount;
    } else {
      calculatedTaxAmount = rate;
      calculatedTotal =
        ratePercentage > 0
          ? parseFloat(amount) + calculatedTaxAmount
          : parseFloat(amount);
    }

    setTaxAmount(calculatedTaxAmount);
    setTotal(calculatedTotal);

    const subscriptionItem = {
      donation_package_id: donationPackages?.id,
      donation_package_name: donationPackages?.name,
      donation_package_code: donationPackages?.code,
      quantity: 1,
      price: amount,
      tax_rate: rate,
      tax_amount: calculatedTaxAmount,
      total: calculatedTotal,
      tax_code: taxCode,
      is_tax_included: donationPackages?.is_tax_included,
      cgst: ratePercentage > 0 ? calculatedTaxAmount / 2 : 0,
      sgst: ratePercentage > 0 ? calculatedTaxAmount / 2 : 0,
    };

    dispatch(updateTotals(subscriptionItem));
  };

  const handleTaxCodeChange = (selectedTaxCode, price) => {
    const selectedTaxOption = taxList.find(
      (option) => option.code === selectedTaxCode
    );

    if (selectedTaxOption) {
      setTaxRate((price * selectedTaxOption.rate) / 100);
    } else {
      setTaxRate(0);
    }

    dispatch(changeDonationItemTaxCode(selectedTaxCode));

    calculateTaxAndTotal(
      price,
      selectedTaxCode,
      donationPackages?.is_tax_included
    );
  };

  useEffect(() => {
    dispatch(
      fetchSetting({
        accessToken: token,
        tenant_id: tenantId,
      })
    );
  }, [tenantId, token]);

  const resetForm = () => {
    formikRef.current?.resetForm();
  };

  useEffect(() => {
    if (response) {
      const voucher =
        response?.data?.donation_subscription_vouchers?.[0]?.donation_voucher;
      const party = response?.data?.party;
      const voucherUrl = voucher?.url;

      if (voucherUrl) {
        dispatch(resetSubscription());

        if (
          voucherUrl.startsWith("http://") ||
          voucherUrl.startsWith("https://")
        ) {
          window.location.href = voucherUrl;
        } else {
          navigate(voucherUrl);
        }
      } else {
        console.warn("Voucher URL not found in response.");
      }
    }
  }, [response]);

  if (showError) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <div className="text-center p-4 border rounded shadow-sm bg-white">
          <img
            src={errorImage}
            alt="Error"
            style={{ width: "80px", marginBottom: "20px" }}
          />
          <h3 className="text-danger mb-3">Invalid or Missing Link</h3>
          <p className="text-muted">
            The URL you used is missing a required parameter or is not valid.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Container fluid className="py-4" style={{ height: 'calc(100vh - 20px)', overflowY: 'scroll', overflowX: 'hidden' }}>
        <div className="d-flex flex-column align-items-center w-100">
          <div className="form-container shadow-md py-4 px-3 px-md-5 w-100" style={{ maxWidth: '500px' }}>
            <Formik
              innerRef={formikRef}
              initialValues={subscriptionState}
              validationSchema={Yup.object().shape({
                party: Yup.object().shape({
                  name: Yup.string().required("Please enter a name"),
                  mobile: Yup.string()
                    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
                    .required("Please enter your mobile number"),
                }),
              })}
              onSubmit={async (values, { setSubmitting }) => {
                setLoading(true);
                let data = cloneDeep(values);
                 data.donation_subscription_items = {
                  
                    donation_package_id: donationPackages?.id,
                    donation_package_name: donationPackages?.name,
                    donation_package_code: donationPackages?.code,
                    quantity: 1,
                    price: price,
                    tax_total: taxAmount,
                    tax_amount: taxAmount,
                    tax_code: values.donation_subscription_items.tax_code,
                    is_tax_included: donationPackages?.is_tax_included,
                    
                 };
                data.grand_total = data.donation_subscription_items.price;
                data.donation_subscription_number = subscriptionState.donation_subscription_number;
                try {
                  const response = await axios.post(
                    `${configData.API_URL}donations/donation-subscriptions`,
                    data,
                    {
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "X-Tenant": tenantId,
                      },
                    }
                  );
                  setResponse(response.data);
                  setLoading(false);
                  resetForm();
                } catch (error) {
                  console.error("Error submitting form:", error);
                }
                setSubmitting(false);
              }}
            >
              {({
                values,
                handleBlur,
                handleChange,
                handleSubmit,
                errors,
                touched,
              }) => (
                <>
                  <Row>
                    <Col xs={12} className="d-flex justify-content-center">
                      <Image
                        src={businessDetails?.logo}
                        height={60}
                        style={{ width: "auto", objectFit: "contain" }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <div className="d-flex flex-column align-items-center text-center">
                        <text className="bold fs-3">
                          {businessDetails?.business_name}
                        </text>
                        <text>{businessDetails?.business_address},</text>
                        <text>
                          {businessDetails?.city}, {findState?.name} -{" "}
                          {businessDetails?.pincode}.
                        </text>
                        {businessDetails?.business_phone && (
                          <text className="bold">
                            Mobile :{" "}
                            <span style={{ fontWeight: 400 }}>
                              {businessDetails?.business_phone}
                            </span>
                          </text>
                        )}
                        {businessDetails?.gstin && (
                          <text className="bold">
                            GSTIN : <span>{businessDetails.gstin}</span>
                          </text>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <hr />
                  {!showSuccessModal && (
                    <Form onSubmit={handleSubmit}>

                      <Row >
                        <Col className="mb-3" xs={12} md={6} lg={6}>
                          <Form.Group>
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                              name="party.name"
                              placeholder="Enter name"
                              value={values.party.name}
                              onChange={(e) => {
                                handleChange(e);
                                dispatch(changePartyName(e.target.value));
                              }}
                              onBlur={handleBlur}
                            />
                            {touched.party?.name && errors.party?.name && (
                              <Form.Text className="text-danger">{errors.party.name}</Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                        <Col className="mb-3" xs={12} md={6} lg={6}>
                          <Form.Group>
                            <Form.Label>Mobile</Form.Label>
                            <Form.Control
                              name="party.mobile"
                              placeholder="Enter mobile number"
                              value={values.party.mobile}
                              onChange={(e) => {
                                handleChange(e);
                                dispatch(changePartyMobile(e.target.value));
                              }}
                              onBlur={handleBlur}
                              maxLength="10"
                            />
                            {touched.party?.mobile && errors.party?.mobile && (
                              <Form.Text className="text-danger">{errors.party.mobile}</Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row >
                        <Col className="mb-3" xs={12}>
                          <Form.Group>
                            <Form.Label>Address</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              name="party.address"
                              placeholder="Enter address"
                              value={values.party.address}
                              onChange={(e) => {
                                handleChange(e);
                                dispatch(changePartyAddress(e.target.value));
                              }}
                              onBlur={handleBlur}
                            />
                            {touched.party?.address && errors.party?.address && (
                              <Form.Text className="text-danger">{errors.party.address}</Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row >
                        <Col className="mb-3" xs={12}>
                          <Form.Group>
                            <Form.Label>Payment Terms</Form.Label>
                            <div className="border rounded p-3">
                              <div className="d-flex gap-3 flex-wrap">
                                <Form.Check
                                  inline
                                  type="radio"
                                  label="One-Time"
                                  value="one-time"
                                  checked={values.type === "one-time"}
                                  onChange={(e) => {
                                    handleChange(e);
                                    dispatch(changeType("one-time"));
                                  }}
                                />
                                <Form.Check
                                  inline
                                  type="radio"
                                  label="Recurring"
                                  value="recurring"
                                  checked={values.type === "recurring"}
                                  onChange={(e) => {
                                    handleChange(e);
                                    dispatch(changeType("recurring"));
                                  }}
                                />
                              </div>

                              {values.type === "recurring" ? (
                                <Row className=" g-3">
                                  <Col className="mb-3" xs={12} sm={4} md={3}>
                                    <Form.Label>Donate Every</Form.Label>
                                    <Form.Control
                                      type="number"
                                      value={values.interval}
                                      onChange={(e) => {
                                        handleChange(e);
                                        dispatch(changeInterval(e.target.value));
                                      }}
                                    />
                                  </Col>

                                  <Col className="mb-3" xs={12} sm={4} md={3}>
                                    <Form.Select
                                      name="interval_unit"
                                      value={values.interval_unit}
                                      onChange={(e) => {
                                        handleChange(e);
                                        dispatch(changeIntervalUnit(e.target.value));
                                      }}
                                    >
                                      <option value="month(s)">Month(s)</option>
                                      <option value="year(s)">Year(s)</option>
                                    </Form.Select>
                                  </Col>

                                  <Col className="mb-3" xs={12} sm={6} md={6}>
                                    <Form.Label>Start Date</Form.Label>
                                    <DatePicker
                                      className="form-control"
                                      selected={values.start_date}
                                      onChange={(date) => dispatch(changeStartDate(date))}
                                      dateFormat="dd/MM/yyyy"
                                      minDate={new Date()}
                                    />
                                  </Col>
                                </Row>
                              ) : (
                                <Row className="mt-2 g-3">
                                  <Col className="mb-3" xs={6} sm={6} md={3}>
                                    <Form.Label className="mb-0">Every</Form.Label>
                                    <Form.Control plaintext readOnly defaultValue="N/A" />
                                  </Col>
                                  <Col className="mb-3" xs={6} sm={6} md={5}>
                                    <Form.Label className="mb-0">Date</Form.Label>
                                    <Form.Control
                                      plaintext
                                      readOnly
                                      defaultValue={new Date().toLocaleDateString("en-GB")}
                                    />
                                  </Col>
                                </Row>
                              )}
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row >
                        <Col className="mb-3" xs={12} md={6} >
                          <Form.Group>
                            <Form.Label>
                              Donate for <span className="fw-bold">{donationPackages.name}</span>
                            </Form.Label>
                            <Form.Control
                              type="number"
                              placeholder={`Please enter at least ${donationPackages?.min_amount}`}
                              value={price}
                              onChange={(e) => {
                                const priceValue = parseFloat(e.target.value);
                                setPrice(priceValue);
                                dispatch(changeDonationItemPrice(priceValue));
                                calculateTaxAndTotal(
                                  priceValue,
                                  values.donation_subscription_items.tax_code,
                                  donationPackages?.is_tax_included
                                );
                              }}
                            />
                          </Form.Group>
                        </Col>

                        {donationPackages?.is_tax_included == 1 && (
                        <Col className="mb-3" xs={12} md={6}>
                          <Form.Group>
                            <Form.Label>TAX (GST)</Form.Label>
                            <div className="d-flex align-items-center form-control">
                              <Form.Select
                                value={values.donation_subscription_items.tax_code}
                                onChange={(e) => {
                                  const taxCode = e.target.value;
                                  dispatch(changeDonationItemTaxCode(taxCode));
                                  handleTaxCodeChange(
                                    taxCode,
                                    values.donation_subscription_items.price
                                  );
                                }}
                                className="border-0 shadow-none p-0"
                                style={{ flex: 1 }}
                              >
                                {taxList.map((option) => (
                                  <option key={option.code} value={option.code}>
                                    {option.label}
                                  </option>
                                ))}
                              </Form.Select>
                              <span className="fw-bold ps-3">{taxRate.toFixed(2)}</span>
                            </div>
                          </Form.Group>
                        </Col>
                        )}
                      </Row>
                      <Row className="d-flex align-items-center justify-content-between mt-5 mb-2">
                        <Col xs={6} md={6}>
                          <p className="fw-bold text-success fs-5 mb-0">
                            Total Amount: {businessDetails?.currency}{" "}
                            {values.donation_subscription_items.price || "0"}
                          </p>
                        </Col>
                        <Col xs={6} md={6} className="d-flex justify-content-end">
                          <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? (
                              <Spinner animation="border" size="sm" className="me-2" />
                            ) : (
                              "Donate"
                            )}
                          </Button>
                        </Col>
                      </Row>

                    </Form>

                  )}
                </>
              )}
            </Formik>
          </div>

          <div className="w-100 mt-4">
            <FooterSection />
          </div>
        </div>
      </Container>
    </>
  );
};

export default DonationFormPage;
